"use client";
// Toast — thông báo nổi góc DƯỚI-PHẢI, tự ẩn ~3.5s, xếp chồng, đóng tay.
// Thay cho alert() trình duyệt. Dùng: const { success, error, info } = useToast().
import * as React from "react";
import { CheckCircle2, XCircle, Info, X } from "lucide-react";
import { cn } from "@/lib/utils";

type ToastTone = "success" | "error" | "info";
type ToastItem = { id: number; message: string; tone: ToastTone; duration: number };
type ToastOpts = { tone?: ToastTone; duration?: number };

type ToastCtx = {
  toast: (message: string, opts?: ToastOpts) => void;
  success: (message: string, opts?: Omit<ToastOpts, "tone">) => void;
  error: (message: string, opts?: Omit<ToastOpts, "tone">) => void;
  info: (message: string, opts?: Omit<ToastOpts, "tone">) => void;
};

const Ctx = React.createContext<ToastCtx | null>(null);

const DEFAULT_DURATION = 3500;

const TONE: Record<
  ToastTone,
  { icon: React.ComponentType<{ className?: string }>; ring: string; iconCls: string; role: "status" | "alert" }
> = {
  success: {
    icon: CheckCircle2,
    ring: "border-green-500/40",
    iconCls: "text-green-600 dark:text-green-400",
    role: "status",
  },
  error: {
    icon: XCircle,
    ring: "border-red-500/40",
    iconCls: "text-red-600 dark:text-red-400",
    role: "alert",
  },
  info: {
    icon: Info,
    ring: "border-brand-2/30",
    iconCls: "text-brand-ink",
    role: "status",
  },
};

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = React.useState<ToastItem[]>([]);
  const counter = React.useRef(0); // id tăng dần, không phụ thuộc Math.random/Date.now

  const dismiss = React.useCallback((id: number) => {
    setItems((list) => list.filter((t) => t.id !== id));
  }, []);

  const push = React.useCallback(
    (message: string, opts?: ToastOpts) => {
      const id = ++counter.current;
      const tone = opts?.tone ?? "info";
      const duration = opts?.duration ?? DEFAULT_DURATION;
      setItems((list) => [...list, { id, message, tone, duration }]);
    },
    [],
  );

  const api = React.useMemo<ToastCtx>(
    () => ({
      toast: push,
      success: (m, o) => push(m, { ...o, tone: "success" }),
      error: (m, o) => push(m, { ...o, tone: "error" }),
      info: (m, o) => push(m, { ...o, tone: "info" }),
    }),
    [push],
  );

  return (
    <Ctx.Provider value={api}>
      {children}
      <div
        className="pointer-events-none fixed bottom-4 right-4 z-[100] flex w-[calc(100vw-2rem)] max-w-sm flex-col gap-2"
        aria-live="polite"
      >
        {items.map((t) => (
          <ToastCard key={t.id} item={t} onDismiss={dismiss} />
        ))}
      </div>
    </Ctx.Provider>
  );
}

function ToastCard({ item, onDismiss }: { item: ToastItem; onDismiss: (id: number) => void }) {
  const [shown, setShown] = React.useState(false);
  const cfg = TONE[item.tone];
  const Icon = cfg.icon;

  React.useEffect(() => {
    // animate-in: chuyển từ ẩn → hiện ở frame kế.
    const raf = requestAnimationFrame(() => setShown(true));
    const timer = setTimeout(() => onDismiss(item.id), item.duration);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timer);
    };
  }, [item.id, item.duration, onDismiss]);

  return (
    <div
      role={cfg.role}
      className={cn(
        "pointer-events-auto flex items-start gap-3 rounded-lg border bg-card px-4 py-3 text-sm shadow-elevate",
        "transition-all duration-200 ease-out motion-reduce:transition-none",
        cfg.ring,
        shown ? "translate-y-0 opacity-100" : "translate-y-2 opacity-0",
      )}
    >
      <Icon className={cn("mt-0.5 h-[18px] w-[18px] shrink-0", cfg.iconCls)} />
      <div className="min-w-0 flex-1 break-words text-fg">{item.message}</div>
      <button
        type="button"
        aria-label="Đóng thông báo"
        onClick={() => onDismiss(item.id)}
        className="-mr-1 -mt-1 grid h-7 w-7 shrink-0 place-items-center rounded-md text-muted-fg transition hover:bg-muted hover:text-fg"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

export function useToast(): ToastCtx {
  const ctx = React.useContext(Ctx);
  if (!ctx) throw new Error("useToast phải dùng bên trong <ToastProvider>");
  return ctx;
}
